import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { getPaymentStatus } from "@/api";
import { useLang } from "@/i18n";
import { waLink } from "@/config";
import { trackPurchase } from "@/analytics";
import { CheckCircle2, Loader2, AlertTriangle } from "lucide-react";

export default function PaymentSuccessPage() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState("checking"); // checking | paid | error
  const tries = useRef(0);
  const purchaseTracked = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setState("error");
      return;
    }
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      tries.current += 1;
      try {
        const r = await getPaymentStatus(sessionId);
        if (r.payment_status === "paid") {
          if (!purchaseTracked.current) {
            purchaseTracked.current = true;
            trackPurchase({ value: r.amount_mxn || 0 });
          }
          setState("paid");
          return;
        }
        if (["failed", "expired"].includes(r.payment_status) || tries.current >= 8) {
          setState("error");
          return;
        }
      } catch {
        if (tries.current >= 8) {
          setState("error");
          return;
        }
      }
      setTimeout(poll, 2000);
    };
    poll();
    return () => {
      stopped = true;
    };
  }, [sessionId]);

  return (
    <div data-testid="payment-success-page" className="pt-40 pb-32 max-w-2xl mx-auto px-6 text-center">
      {state === "checking" && (
        <>
          <Loader2 size={56} className="mx-auto text-[#C4A661] animate-spin" strokeWidth={1} />
          <h1 className="font-serif-hc text-4xl mt-8 text-[#F5F2EA]" data-testid="payment-checking">{t("booking.pay_checking")}</h1>
        </>
      )}
      {state === "paid" && (
        <>
          <CheckCircle2 size={56} className="mx-auto text-[#C4A661]" strokeWidth={1} />
          <h1 className="font-serif-hc text-5xl mt-8 text-[#F5F2EA]" data-testid="payment-paid">{t("booking.pay_success_title")}</h1>
          <p className="mt-5 text-[#A3A095] leading-relaxed">{t("booking.pay_success_msg")}</p>
        </>
      )}
      {state === "error" && (
        <>
          <AlertTriangle size={56} className="mx-auto text-[#C4A661]" strokeWidth={1} />
          <h1 className="font-serif-hc text-4xl mt-8 text-[#F5F2EA]" data-testid="payment-error">{t("booking.pay_error")}</h1>
        </>
      )}
      <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
        <Link to="/" className="hc-btn-secondary" data-testid="payment-home">Huitzil-Coatl Travels</Link>
        <a href={waLink()} target="_blank" rel="noreferrer" className="hc-btn-wa">WhatsApp</a>
      </div>
    </div>
  );
}
